"use client";

import useFormSubmit from "@/hooks/useApiSubmit";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  DatePickerField,
  Field,
  Fieldset,
  Form,
  PhoneField,
  Select,
  TextArea,
  Typography,
} from "@repo/ui";
import { useRouter } from "next/navigation";
import { useMemo } from "react";
import * as Yup from "yup";
import { useAppDispatch } from "@/store/hooks";
import { setNameAndMessage } from "@/store/features/appSlice";
import useLocations from "@/hooks/useLocations";
import useCategories from "@/hooks/useCategories";
import TermsCheckbox from "./TermsCheckbox";
import { useCountryCode } from "@/hooks/useCountryCode";

const QueryFormSchema = Yup.object().shape({
  fullName: Yup.string()
    .required()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  contactNumber: Yup.string()
    .required()
    .min(10, "Too Short!")
    .max(16, "Too Long!")
    .required("Required"),
  email: Yup.string().email("Invalid email").required("Required"),
});

const requirementTypes = [
  { value: "corporate-training", label: "Corporate Training" },
  { value: "consulting-services", label: "Consulting Services" },
  { value: "certification-program", label: "Certification Program" },
  {
    value: "both-training-and-consulting",
    label: "Both Training and Consulting",
  },
  { value: "others", label: "Others" },
];

const deliveryModes = [
  { value: "classroom-on-site", label: "Classroom (On-site)" },
  { value: "virtual-instructor-led", label: "Virtual Instructor-Led" },
  { value: "self-paced-elearning", label: "Self-Paced / eLearning" },
  { value: "hybrid", label: "Hybrid" },
];

const participants = [
  {
    label: "1",
    value: "1",
  },
  {
    label: "2",
    value: "2",
  },
  {
    label: "3",
    value: "3",
  },
  {
    label: "4",
    value: "4",
  },
  {
    label: "5",
    value: "5",
  },
  {
    label: "6",
    value: "6",
  },
  {
    label: "7",
    value: "7",
  },
  {
    label: "8",
    value: "8",
  },
  {
    label: "9",
    value: "9",
  },
  {
    label: "10",
    value: "10",
  },
  {
    label: "10+",
    value: "10+",
  },
  {
    label: "15+",
    value: "15+",
  },
  {
    label: "20+",
    value: "20+",
  },
  {
    label: "30+",
    value: "30+",
  },
  {
    label: "40+",
    value: "40+",
  },
  {
    label: "60+",
    value: "60+",
  },
  {
    label: "80+",
    value: "80+",
  },
  {
    label: "100+",
    value: "100+",
  },
  {
    label: "150+",
    value: "150+",
  },
  {
    label: "200+",
    value: "200+",
  },
  {
    label: "300+",
    value: "300+",
  },
  {
    label: "400+",
    value: "400+",
  },
  {
    label: "500+",
    value: "500+",
  },
  {
    label: "600+",
    value: "600+",
  },
  {
    label: "700+",
    value: "700+",
  },
  {
    label: "800+",
    value: "800+",
  },
  {
    label: "900+",
    value: "900+",
  },
  {
    label: "1000+",
    value: "1000+",
  },
];

const DEFAULT_FORM_VALUES = {
  fullName: "",
  jobTitle: "",
  companyName: "",
  email: "",
  contactNumber: "",
  country_id: "",
  requirementType: "",
  interested: "",
  preferredOption: "",
  noOfParticipants: 0,
  requestDate: "",
  filePath: "",
  message: "",
  terms: "false",
  enquiryType: "CONSULTATION_PROPOSAL_FORM",
};

const ConsultationProposalForm = () => {
  const { countries, setSelectedCountry, loading } = useLocations();
  const { data: categories, loading: categoryLoading } = useCategories();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { country } = useCountryCode();

  const { handleSubmit } = useFormSubmit({
    onSuccess: (data) => {
      dispatch(setNameAndMessage(data.response));
      router.push("/thank-you-for-enquiry");
    },
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  const initialValues = useMemo(
    () => ({
      ...DEFAULT_FORM_VALUES,
    }),
    []
  );

  const formContent = useMemo(() => {
    // Calculate tomorrow's date at midnight
    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return (
      <Form
        initialValues={initialValues}
        validationSchema={QueryFormSchema}
        onSubmit={handleSubmit}
        encType="multipart/form-data"
      >
        {({ values, handleChange, setFieldValue, isSubmitting }) => (
          <div>
            <Fieldset columns={2}>
              <Field
                required
                name="fullName"
                label="Full Name"
                value={values.fullName}
                onChange={handleChange}
              />
              <Field
                label="Designation"
                name="jobTitle"
                value={values.jobTitle}
                onChange={handleChange}
              />

              <Field
                label="Company Name"
                name="companyName"
                value={values.companyName}
                onChange={handleChange}
              />

              <Field
                required
                label="Official Email Address"
                name="email"
                value={values.email}
                onChange={handleChange}
              />

              <PhoneField
                label="Phone Number"
                name="contactNumber"
                required
                value={values.contactNumber}
                onChange={(phone: string) =>
                  setFieldValue("contactNumber", phone)
                }
                defaultCountry={country}
              />

              {/* Country list  */}
              <Select
                label="Location / Country"
                name="country_id"
                loading={loading}
                value={values.country_id}
                options={countries.map((item) => ({
                  value: item.id,
                  label: item.name,
                }))}
                onChange={(val) => {
                  setSelectedCountry(val);
                  setFieldValue("country_id", val);
                }}
              />
              {/* Type of Requirement */}
              <Select
                label="Type of Requirement"
                name="requirementType"
                value={values.requirementType}
                options={requirementTypes}
                onChange={(val) => {
                  setFieldValue("requirementType", val);
                }}
              />

              {/* Area of Interest / Training Domain  */}
              <Select
                label="Area of Interest / Training Domain"
                name="interested"
                loading={categoryLoading}
                value={values.interested}
                options={categories}
                onChange={(val) => {
                  setFieldValue("interested", val);
                }}
              />
              {/* Preferred Mode of Delivery */}
              <Select
                label="Preferred Mode of Delivery"
                name="preferredOption"
                value={values.preferredOption}
                options={deliveryModes}
                onChange={(val) => {
                  setFieldValue("preferredOption", val);
                }}
              />

              {/* Estimated No. of Participants */}
              <Select
                label="Estimated No. of Participants"
                name="noOfParticipants"
                value={values.noOfParticipants}
                options={participants}
                onChange={(val) => {
                  setFieldValue("noOfParticipants", val);
                }}
              />

              {/* Preferred Training Start Date */}
              <DatePickerField
                label="Preferred Training Start Date"
                value={values.requestDate}
                onChange={(value) => setFieldValue("requestDate", value)}
                calenderProps={{ disabled: { before: tomorrow } }}
              />
              {/* Upload RFQ / RFP / RFI */}
              <Field
                label="Upload RFQ / RFP / RFI"
                name="filePath"
                type="file"
                inputProps={{
                  accept:
                    ".doc,.docx,.xml,.pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf",
                }}
                onChange={(event) => {
                  const target = event.target as unknown as HTMLInputElement;
                  const file = target.files?.[0];
                  if (file) {
                    setFieldValue("filePath", file);
                  }
                }}
              />
              {/* Describe Your Requirement * */}
              <TextArea
                name="message"
                label="Describe Your Requirement"
                colSpan={2}
                value={values.message}
                placeholder="Share more context on your internal training or consultation requirement..."
                onChange={handleChange}
              />
            </Fieldset>

            <TermsCheckbox
              onCheckedChange={(value) => setFieldValue("terms", value)}
              value={values.terms}
            />
            <div className="text-right mt-4">
              <Button type="submit" color="primary" loading={isSubmitting}>
                Submit Enquiry
              </Button>
            </div>
            {/* <pre>{JSON.stringify(values, null, 2)}</pre> */}
          </div>
        )}
      </Form>
    );
  }, [
    country,
    handleSubmit,
    initialValues,
    categories,
    categoryLoading,
    countries,
    loading,
    setSelectedCountry,
  ]);

  return (
    <Card>
      <CardHeader className="!p-8 !pb-3">
        <Typography variant="h4">
          Request a Customized Training or Consultation Proposal
        </Typography>

        <Typography paragraph className="!text-sm text-gray-500">
          Let us know your specific needs. Our team will get back to you with a
          tailored solution.
        </Typography>
      </CardHeader>
      <CardContent className="!p-8 !pt-0">{formContent}</CardContent>
    </Card>
  );
};

export default ConsultationProposalForm;
